import { lazy, Suspense } from "react";

import { useHasOpened } from "~/hooks/useHasOpened.ts";
import type { TimeTrackingController } from "~/modules/timeTracking/hooks/useTimeTrackingActions.ts";

// The sidebars hold the time tracking forms. Each loads on its first opening, not with the page that mounts them.
const CreateTimeTrackingSidebar = lazy(() => import("~/modules/timeTracking/components/CreateTimeTrackingSidebar"));
const DeleteTimeTrackingModal = lazy(() => import("~/modules/timeTracking/components/DeleteTimeTrackingModal"));
const UpdateTimeTrackingSidebar = lazy(() => import("~/modules/timeTracking/components/UpdateTimeTrackingSidebar"));

type Props = {
	controller: TimeTrackingController;
	/** The dashboard calendar has its own create sidebar, with the absence form. */
	hideCreate?: boolean;
};

/** The create sidebar, the update sidebar and the delete dialog of a time tracking view. Mount it once for each view. */
export function TimeTrackingActions(props: Props) {
	const { controller, hideCreate } = props;
	const { queries } = controller;
	const locations = queries.locations.data ?? [];
	const quotas = queries.activeStaffingsAndQuotas.data?.quotas ?? {};
	const timeTrackingTypes = queries.timeTrackingTypes.data ?? [];
	const users = queries.users.data ?? [];
	const userDeliverables = queries.userDeliverables.data ?? [];
	const userLocations = queries.userLocations.data ?? [];
	const timeTrackingToUpdate = controller.timeTrackingToUpdate;

	const hasOpenedCreate = useHasOpened(controller.selectedDate !== null);
	const hasOpenedUpdate = useHasOpened(!!timeTrackingToUpdate);
	const hasOpenedDelete = useHasOpened(!!controller.timeTrackingToDelete);

	return (
		<>
			{!hideCreate && hasOpenedCreate && <Suspense fallback={null}><CreateTimeTrackingSidebar
				activeStaffings={controller.openActiveStaffings}
				nonProjectIsClosed={controller.nonProjectIsClosed}
				userDeliverables={userDeliverables}
				userLocations={userLocations}
				trackedStaffingIds={controller.trackedStaffingIdsOnSelectedDay}
				close={controller.closeCreate}
				isOpen={controller.selectedDate !== null}
				date={controller.selectedDate}
				locations={locations}
				quotas={quotas}
				selectedUserId={controller.userId}
				timeTrackingTypes={timeTrackingTypes}
				users={users}
				userIsFreelancer={controller.userIsFreelancer}
			/></Suspense>}

			{hasOpenedUpdate && <Suspense fallback={null}><UpdateTimeTrackingSidebar
				activeStaffings={controller.activeStaffings}
				userDeliverables={userDeliverables}
				userLocations={userLocations}
				close={controller.closeUpdate}
				isOpen={!!timeTrackingToUpdate}
				date={timeTrackingToUpdate?.date ? new Date(timeTrackingToUpdate.date) : null}
				locations={locations}
				quotas={quotas}
				trackedStaffingIds={controller.trackedStaffingIdsOnUpdateDay}
				timeTrackingData={timeTrackingToUpdate}
				selectedUserId={controller.userId}
				timeTrackingTypes={timeTrackingTypes}
				users={users} /></Suspense>}

			{hasOpenedDelete && <Suspense fallback={null}><DeleteTimeTrackingModal
				isOpen={!!controller.timeTrackingToDelete}
				onClose={controller.closeDelete}
				timeTrackingData={controller.timeTrackingToDelete} /></Suspense>}
		</>
	);
}
